/** Two paired inputs (FR + EN) that update a single { fr, en } value.
 *  Use `type="textarea"` for multiline. */
export function BilingualField({
    label,
    value,
    onChange,
    type = "text",
    placeholder,
    required = false,
    rows = 3,
    idPrefix,
    hint,
}) {
    const v = value || { fr: "", en: "" };
    const upd = (key) => (e) =>
        onChange({ ...v, [key]: e.target.value });

    return (
        <div className="mb-6">
            {label && (
                <div className="eyebrow mb-2 flex items-center gap-2">
                    {label} {required && <span className="text-red-400">*</span>}
                </div>
            )}
            {hint && (
                <p className="text-xs text-neutral-500 mb-3 leading-relaxed">
                    {hint}
                </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["fr", "en"].map((lang) => (
                    <div key={lang}>
                        <div className="text-[0.6rem] tracking-widest uppercase text-neutral-500 mb-1">
                            {lang === "fr" ? "Français" : "English"}
                        </div>
                        {type === "textarea" ? (
                            <textarea
                                data-testid={`${idPrefix || "bfield"}-${lang}`}
                                rows={rows}
                                required={required && lang === "fr"}
                                className="field-box resize-y"
                                placeholder={placeholder}
                                value={v[lang] || ""}
                                onChange={upd(lang)}
                            />
                        ) : (
                            <input
                                data-testid={`${idPrefix || "bfield"}-${lang}`}
                                type={type}
                                required={required && lang === "fr"}
                                className="field-box"
                                placeholder={placeholder}
                                value={v[lang] || ""}
                                onChange={upd(lang)}
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Simple string / list-of-strings field for a bilingual example_situations block:
 *  value shape: { fr: string[], en: string[] } */
export function BilingualListField({
    label,
    value,
    onChange,
    idPrefix,
    hint,
}) {
    const v = value || { fr: [], en: [] };
    const upd = (lang) => (e) => {
        const text = e.target.value;
        const lines = text
            .split(/\n/)
            .map((s) => s.trim())
            .filter(Boolean);
        onChange({ ...v, [lang]: lines });
    };
    return (
        <div className="mb-6">
            {label && <div className="eyebrow mb-2">{label}</div>}
            {hint && (
                <p className="text-xs text-neutral-500 mb-3 leading-relaxed">
                    {hint}
                </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["fr", "en"].map((lang) => (
                    <div key={lang}>
                        <div className="text-[0.6rem] tracking-widest uppercase text-neutral-500 mb-1">
                            {lang === "fr" ? "Français" : "English"} · une ligne
                            par élément
                        </div>
                        <textarea
                            data-testid={`${idPrefix || "blist"}-${lang}`}
                            rows={6}
                            className="field-box resize-y"
                            value={(v[lang] || []).join("\n")}
                            onChange={upd(lang)}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}
